/**
 * @file PageSettingsPanel.tsx
 * @description «Настройки страницы» — правая панель конструктора, когда на
 * странице ничего не выбрано (phase19.16 Р1-6а; uiux-builder-shell.md §2.2):
 * название, адрес (`магазин.ru/[about]` с предупреждением про старые
 * ссылки), поля для поиска с образцом «Так в поиске», «Не показывать в
 * поиске». Видимость и удаление — `PageVisibilityCard`.
 *
 * Р3а (SB126): правка записывается кнопкой «Сохранить» в черновик страницы —
 * на сайт после «Опубликовать»; первая группа — «Публикации»
 * (`PagePublicationsGroup`). Без права контента — только просмотр.
 * phase19.16 Р2: «Фон страницы» и «Дизайн страницы» (`PageBackgroundCard`);
 * страница места (над/под лентой блога) — только пояснение и видимость места.
 *
 * SB122 (uiux-settings-groups §5, шаг 6; §8 — эталон вида групп): карточки
 * «Страница», «Для поиска», «Фон страницы», «Видимость» — `SettingsGroup` в
 * `SettingsGroups panel="page"`: вид прежний, добавились сворачивание,
 * сводки у свёрнутых, оглавление-пилюли на ПК и одна открытая на телефоне.
 * Кнопки «Сохранить/Отменить» — вне групп, под «Для поиска», как раньше.
 *
 * Р3в (v0.19.64): группа «Для поиска» — `PageSearchGroup` (образец по общим
 * SEO-настройкам магазина, «Ключевые запросы» SEO11 — сохраняются сразу,
 * вне публикации); смена адреса — «со старого адреса будет переадресация»
 * (галочка — в окне публикации, SB131).
 *
 * Ревью партии SB122: неудачное «Сохранить» раскрывает первую группу с
 * ошибкой и даёт фокус полю (общий `OpenGroupRequest`); несохранённые
 * правки при уходе из панели (другой блок, раздел, свёртка колонки)
 * спрашивают «Настройки страницы не сохранены» (`usePanelLeaveGuard`).
 */

import { useEffect, useState } from "react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { OpenGroupRequest, SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { TextField } from "../../site-view/TextField";
import { useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { LeaveConfirmDialog } from "./LeaveConfirmDialog";
import { PageBackgroundCard } from "./PageBackgroundCard";
import { PageSearchGroup } from "./PageSearchGroup";
import { PageVisibilityCard } from "./PageVisibilityCard";
import { PagePublications } from "../publish/PagePublications";
import { PagePublicationsGroup } from "../publish/PagePublicationsGroup";
import { usePanelLeaveGuard } from "../../../hooks/site-builder/usePanelLeaveGuard";
import { ApiError } from "../../../lib/api-client";
import type { SitePageDetail } from "../../../lib/api-site-pages";
import { cleanSlugInput, PAGE_ERROR_FIELDS, PAGE_TITLE_MAX, pageFormErrors, type PageForm, type PageFormErrors } from "../../../lib/site-builder/page-form";
import { keywordsChanged, keywordsInput, keywordsOf, type SeoKeywordsValue } from "../../../lib/site-seo/keywords";
import { siteHost } from "../../../lib/site-view/seo";

function formOf(page: SitePageDetail): PageForm {
  return { title: page.title, slug: page.slug, seoTitle: page.seoTitle ?? "", seoDescription: page.seoDescription ?? "", noIndex: page.noIndex, breadcrumbs: page.breadcrumbs ?? "INHERIT" };
}

/** Группа с ошибкой: «Страница» раньше «Для поиска» (порядок на экране); нет — null. */
function errorGroup(errors: PageFormErrors): "page" | "seo" | null {
  if (errors.title || errors.slug) return "page";
  return errors.seoTitle || errors.seoDescription ? "seo" : null;
}

export function PageSettingsPanel({ page }: { page: SitePageDetail }) {
  const b = useBuilder();
  const view = useSiteView();
  const notify = useNotify();
  const [form, setForm] = useState<PageForm>(() => formOf(page));
  const [keywords, setKeywords] = useState<SeoKeywordsValue>(() => keywordsOf(page));
  const [errors, setErrors] = useState<PageFormErrors>({});
  const [saving, setSaving] = useState(false);
  // Неудачных «Сохранить» — запрос раскрыть группу с ошибкой (свернуть её вручную потом можно).
  const [failed, setFailed] = useState(0);
  const home = page.kind === "SYSTEM_HOME";
  const readOnly = !b.canEditPages;

  useEffect(() => {
    setForm(formOf(page));
    setKeywords(keywordsOf(page));
    setErrors({});
  }, [page]);

  const keywordsDirty = keywordsChanged(keywords, page);
  const formDirty = JSON.stringify(form) !== JSON.stringify(formOf(page));
  const dirty = formDirty || keywordsDirty;
  const patch = (next: Partial<PageForm>) => setForm((prev) => ({ ...prev, ...next }));
  const lock = readOnly ? ("readonly" as const) : undefined;
  const pageSummary = `${form.title.trim() || "без названия"} · ${home ? "главная" : `/${form.slug}`}`;

  /** Сохранить; `true` — сохранено (окно ухода из панели ждёт ответ). */
  async function onSave(): Promise<boolean> {
    const found = pageFormErrors(form, home);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFailed((n) => n + 1);
      return false;
    }
    setSaving(true);
    try {
      // Только ключевые запросы — не правка черновика: шлём лишь их (SEO11, вне публикации).
      if (!formDirty) {
        await b.pages.update(keywordsInput(keywords));
        notify.success("Ключевые запросы сохранены");
        return true;
      }
      await b.pages.update({
        title: form.title.trim(),
        ...(home ? {} : { slug: form.slug }),
        seoTitle: form.seoTitle.trim() || null,
        seoDescription: form.seoDescription.trim() || null,
        noIndex: form.noIndex,
        // SB146: только при изменении — сервер до v0.19.65 поля не знает.
        ...(form.breadcrumbs !== (page.breadcrumbs ?? "INHERIT") ? { breadcrumbs: form.breadcrumbs } : {}),
        ...(keywordsDirty ? keywordsInput(keywords) : {}),
      });
      notify.success("Настройки сохранены в черновик — на сайте после «Опубликовать»");
      return true;
    } catch (err) {
      const mapped = err instanceof ApiError ? PAGE_ERROR_FIELDS[err.code] : undefined;
      if (mapped) {
        setErrors({ [mapped.field]: mapped.text });
        setFailed((n) => n + 1);
      } else notify.error(err instanceof ApiError && err.message ? err.message : "Не удалось сохранить страницу.");
      return false;
    } finally {
      setSaving(false);
    }
  }

  // Уход из панели с правками (другой блок, раздел, свёртка колонки) — спросить.
  const { leave } = usePanelLeaveGuard(dirty && !readOnly, onSave);

  if (page.kind === "SLOT") {
    return (
      <div className="flex flex-col gap-4">
        <p className="type-body text-on-surface-variant">
          Это место для блоков: адреса и полей для поиска у него нет. Правки блоков — в черновике, на сайте после «Опубликовать»; вид самой ленты или списка — в панели «Блог» или «Каталог».
        </p>
        <PagePublications />
      </div>
    );
  }

  return (
    <SettingsGroups panel="page">
      <OpenGroupRequest id={errorGroup(errors)} token={failed || null} focusInvalid />
      <PagePublicationsGroup />
      <SettingsGroup id="page" title="Страница" summary={pageSummary} status={errors.title || errors.slug ? "error" : lock}>
        <TextField label="Название" value={form.title} max={PAGE_TITLE_MAX} error={errors.title} disabled={readOnly} onChange={(title) => patch({ title })} />
        {home ? (
          <p className="type-caption text-on-surface-variant">Адрес главной — {siteHost(view.siteUrl)}/, он не меняется.</p>
        ) : (
          <TextField
            label={`Адрес: ${siteHost(view.siteUrl)}/…`}
            value={form.slug}
            error={errors.slug}
            disabled={readOnly}
            hint={form.slug !== page.slug ? "Новый адрес начнёт работать после «Опубликовать»; со старого адреса будет переадресация (галочка — в окне публикации). Пункт меню обновится сам." : "Латиница, цифры и дефис."}
            onChange={(slug) => patch({ slug: cleanSlugInput(slug) })}
          />
        )}
      </SettingsGroup>
      <PageSearchGroup page={page} form={form} patch={patch} errors={errors} keywords={keywords} onKeywords={setKeywords} readOnly={readOnly} />
      {readOnly ? (
        <p className="type-caption text-on-surface-variant">Страницы меняет сотрудник с правом на контент сайта.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button disabled={!dirty} loading={saving} onClick={() => void onSave()}>
            Сохранить
          </Button>
          {dirty ? (
            <Button
              variant="ghost"
              onClick={() => {
                setForm(formOf(page));
                setKeywords(keywordsOf(page));
              }}
            >
              Отменить
            </Button>
          ) : null}
        </div>
      )}
      {readOnly ? null : <PageBackgroundCard page={page} />}
      <PageVisibilityCard page={page} />
      <LeaveConfirmDialog leave={leave} title="Настройки страницы не сохранены" description="Название, адрес или поля для поиска изменены. Сохранить их перед переходом?" />
    </SettingsGroups>
  );
}
